import { Controller } from "@hotwired/stimulus"
import Chart from "chart.js/auto"
import { leadDrill } from "../lead_drill"

/**
 * Generic Chart.js lifecycle wrapper.
 *
 * Attached to a <canvas>. Reads chart configuration from data-* attributes,
 * instantiates a Chart.js chart on connect, and destroys it on disconnect
 * (plus on turbo:before-cache) to prevent memory leaks and stale canvases
 * in Turbo's page cache.
 *
 * Supported types: line, bar, column, area, matrix, sparkline.
 *
 * Note: Global Chart.js setup (MatrixController, ChartDataLabels,
 * annotationPlugin, nautical defaults) lives in app/frontend/packs/application.js
 * (Task 4). Importing `chart.js/auto` here shares the same Chart registry,
 * so those global defaults and plugins are automatically available.
 */
export default class extends Controller {
  // `data` is intentionally a String here even though the upstream callers
  // sometimes serialize an Object ({ labels, datasets }) and sometimes an
  // Array ([{date, hour, count}, ...] for matrix heatmaps). Stimulus's
  // `Object` value type strict-rejects arrays at connect-time, so we accept
  // the raw JSON string and parse it ourselves below — that lets a single
  // controller drive both shape families without forking per-type controllers.
  static values = {
    type:    { type: String, default: "line" },
    data:    { type: String, default: "{}" },
    options: { type: Object, default: {} }
  }

  connect() {
    this.chart = this._buildChart()
    this._registerTurboCacheCleanup()
  }

  // Centralized accessor used by every _*Config helper below. The raw
  // value comes through Stimulus as a JSON string (see the `static values`
  // note above); we parse it on demand and cache the result so a controller
  // built once on connect() doesn't re-parse on every internal lookup.
  _data() {
    if (this._dataCache !== undefined) return this._dataCache
    const raw = this.dataValue
    if (raw == null || raw === "") {
      this._dataCache = {}
      return this._dataCache
    }
    try {
      this._dataCache = JSON.parse(raw)
    } catch (e) {
      console.warn("chart controller: data-chart-data-value is not valid JSON:", e.message)
      this._dataCache = {}
    }
    return this._dataCache
  }

  disconnect() {
    this._destroyChart()
    document.removeEventListener("turbo:before-cache", this._onBeforeCache)
    document.removeEventListener("turbo:render", this._onTurboRender)
  }

  _buildChart() {
    const canvas = this.element
    if (!(canvas instanceof HTMLCanvasElement)) {
      console.warn("chart controller attached to non-canvas element:", canvas)
      return null
    }

    const ctx = canvas.getContext("2d")
    const config = this._configFor(this.typeValue, ctx)

    return new Chart(ctx, {
      type: config.type,
      data: config.data,
      options: this._mergeOptions(config.options, this.optionsValue)
    })
  }

  _configFor(type, ctx) {
    switch (type) {
      case "sparkline":
        return this._sparklineConfig()
      case "area":
        return this._areaConfig(ctx)
      case "matrix":
        return this._matrixConfig()
      case "column":
        // Chart.js has no native "column" — it's just a vertical bar chart.
        return { type: "bar", data: this._data(), options: this._defaultOptions() }
      default:
        return { type, data: this._data(), options: this._defaultOptions() }
    }
  }

  _defaultOptions() {
    return {
      responsive: true,
      maintainAspectRatio: false
    }
  }

  _sparklineConfig() {
    // Input is typically a {date => count} hash or a Chartkick-style array.
    // Normalize to a labels + values array.
    const series = this._normalizeSeries(this._data())
    const labels = series.labels.map((label) => this._sparkDayLabel(label))
    const values = series.values
    // One day has no line to draw, and a hidden point looks like an empty
    // cell. Show a dot. Two or more days already draw a line.
    const pointRadius = values.length < 2 ? 3 : 0

    // Single-series sparkline → CHART_PRIMARY (Tailwind sky-600 #0284C7),
    // matching ReportsHelper::CHART_PRIMARY in app/helpers/reports_helper.rb.
    // Brand teal #295D77 stays in chrome only — the sparkline strokes are
    // chart fills, so they move to the chart-primary swap color.
    return {
      type: "line",
      data: {
        labels,
        datasets: [{
          data: values,
          borderColor: "#0284C7",
          borderWidth: 1.5,
          tension: 0.3,
          fill: false,
          pointRadius,
          pointHoverRadius: 4,
          pointBackgroundColor: "#0284C7"
        }]
      },
      options: {
        responsive: false,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: { display: false },
          // The canvas is 30px tall. Chart.js paints its tooltip on that
          // canvas, so the count line was clipped under the day. Draw the
          // note in the page instead, as one line: "Oct 6: 4 leads".
          tooltip: {
            enabled: false,
            external: (context) => this._sparkExternalTip(context)
          },
          datalabels: { display: false }
        },
        scales: {
          x: { display: false },
          y: { display: false }
        },
        elements: {
          point: { radius: pointRadius },
          line: { borderWidth: 1.5 }
        }
      }
    }
  }

  // One hover line. The series counts leads, so the word stays "leads".
  _sparkTipText(item) {
    const day = item && item.label != null ? String(item.label) : ""
    const raw = item && item.parsed ? Number(item.parsed.y) : NaN
    const count = Number.isFinite(raw) ? raw : 0
    return `${day}: ${count} leads`
  }

  _sparkExternalTip(context) {
    const tooltip = context && context.tooltip
    const chart = context && context.chart
    const canvas = chart && chart.canvas
    if (!this._sparkTipEl) {
      const el = document.createElement("div")
      el.className = "rep-sparkline-tip"
      el.setAttribute("role", "tooltip")
      el.style.position = "fixed"
      el.style.zIndex = "1080"
      el.style.pointerEvents = "none"
      el.style.background = "#1B1D26"
      el.style.color = "#fff"
      el.style.font = "600 12px Inter, sans-serif"
      el.style.lineHeight = "16px"
      el.style.padding = "4px 8px"
      el.style.borderRadius = "6px"
      el.style.whiteSpace = "nowrap"
      el.style.display = "none"
      document.body.appendChild(el)
      this._sparkTipEl = el
    }
    const el = this._sparkTipEl
    const item = tooltip && tooltip.dataPoints && tooltip.dataPoints[0]
    if (!tooltip || !tooltip.opacity || !item || !canvas) {
      el.style.display = "none"
      return
    }
    el.textContent = this._sparkTipText(item)
    el.style.display = "block"
    const canvasBox = canvas.getBoundingClientRect()
    const tipBox = el.getBoundingClientRect()
    // Grow left of the point. The chat bubble sits on the right, and a
    // note that grows right gets its last letters covered on a phone.
    let left = canvasBox.left + (tooltip.caretX || 0) - tipBox.width - 8
    let top = canvasBox.top - tipBox.height - 6
    if (top < 8) top = canvasBox.bottom + 6
    if (left < 8) left = 8
    const maxLeft = window.innerWidth - tipBox.width - 8
    if (left > maxLeft) left = maxLeft
    el.style.left = `${Math.round(left)}px`
    el.style.top = `${Math.round(top)}px`
  }

  // "2026-10-06" becomes "Oct 6". Anything else is left as written.
  _sparkDayLabel(label) {
    const match = String(label).match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (!match) return String(label)
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
    return `${months[Number(match[2]) - 1]} ${Number(match[3])}`
  }

  _areaConfig(ctx) {
    const base = this._data()
    // CHART_PRIMARY (#0284C7 = rgb(2, 132, 199)) — single-series area fills
    // moved off muted brand teal #295D77 onto the vibrant chart-primary
    // swap color. Mirror in ReportsHelper::CHART_PRIMARY (Ruby side).
    const gradient = ctx.createLinearGradient(0, 0, 0, ctx.canvas.height || 300)
    gradient.addColorStop(0, "rgba(2, 132, 199, 0.3)")
    gradient.addColorStop(1, "rgba(2, 132, 199, 0)")

    const datasets = Array.isArray(base.datasets)
      ? base.datasets.map(ds => ({
          ...ds,
          fill: true,
          backgroundColor: ds.backgroundColor || gradient,
          borderColor: ds.borderColor || "#0284C7",
          tension: 0.4
        }))
      : []

    return {
      type: "line",
      data: { ...base, datasets },
      options: this._defaultOptions()
    }
  }

  _matrixConfig() {
    // Phase 1 input shape (Lead Activity tab, emitted by _lead_activity.html.erb):
    //   [{ date: "2026-04-25", hour: 9, count: 12 }, ...]
    //
    // Phase 2 input shape (Task 6 Qualifier × Hour heatmap):
    //   [{ x: "Mon 09:00", y: "Nova X300...", v: 14, y_full: "Nova X300 line" }, ...]
    //
    // Chart.js matrix expects datasets: [{ data: [{x, y, v}, ...] }].
    //
    // When `options.scale.stops` is supplied (Phase 2) the cell color is an
    // interpolated rgba across the stops; otherwise we keep the legacy
    // single-hue alpha ramp so the Phase 1 Lead Activity heatmap is unchanged.
    const parsed = this._data()
    const raw    = Array.isArray(parsed) ? parsed : []
    const opts  = this.optionsValue || {}
    const scale = opts.scale || {}
    const hasStops = Array.isArray(scale.stops) && scale.stops.length > 0
    const stops    = hasStops ? scale.stops : ['#EAEFF2', '#295D77']
    const yField   = opts.y_full_field || 'y_full'

    // Detect Phase 2 pre-shaped points ({x, y, v}) vs Phase 1 ({date, hour, count}).
    const points = raw.map(p => {
      if (Object.prototype.hasOwnProperty.call(p, 'x') && Object.prototype.hasOwnProperty.call(p, 'y')) {
        return { x: p.x, y: p.y, v: p.v, _full: p[yField] }
      }
      return { x: p.date, y: p.hour, v: p.count }
    })
    const counts = points.map(p => +p.v).filter(n => Number.isFinite(n))
    const maxFromData = counts.length ? Math.max(...counts) : 1
    const maxV = Number.isFinite(+scale.max) && +scale.max > 0 ? +scale.max : Math.max(1, maxFromData)

    const titleT = opts.tooltip?.title_template
    const labelT = opts.tooltip?.label_template

    const isPhase2 = hasStops

    // Derive ordered unique label arrays from the raw data so category scales
    // have an explicit `labels` property (Chart.js Scale#getLabels returns a
    // new [] on every call when none is set — categories never accumulate).
    let xLabels, yLabels
    if (raw.length === 0) {
      xLabels = []
      yLabels = []
    } else if (isPhase2) {
      // Phase 2: x = hour labels, y = day labels. Sort on the RAW `hour` /
      // `date` identifiers that ride along on each point, never on the
      // human-facing strings — "10 AM" sorts before "9 AM", and "May 3"
      // before "May 1". The sort keys are only trustworthy if EVERY point
      // carries one (a single missing key would silently reorder the rest),
      // so an incomplete set falls back to first-appearance order.
      const sortedBy = (key, compare, pick) => {
        const usable = raw.every(p => compare.usable(p[key]))
        const src = usable ? [...raw].sort((a, b) => compare.cmp(a[key], b[key])) : raw
        return [...new Set(src.map(pick))]
      }
      xLabels = sortedBy('hour', { usable: (v) => Number.isFinite(v), cmp: (a, b) => a - b }, p => p.x)
      yLabels = sortedBy('date', { usable: (v) => typeof v === 'string', cmp: (a, b) => a.localeCompare(b) }, p => p.y)
    } else {
      // Phase 1: x-axis = dates (first-appearance order), y-axis is linear — no labels.
      xLabels = [...new Set(raw.map(p => p.date))]
      yLabels = []
    }

    return {
      type: "matrix",
      data: {
        datasets: [{
          label: isPhase2 ? "Value" : "Leads",
          data: points,
          backgroundColor: (context) => {
            const value = +context.raw?.v || 0
            if (hasStops) {
              const t = Math.max(0, Math.min(1, value / maxV))
              return this._interpolateStops(stops, t)
            }
            const alpha = maxV > 0 ? Math.min(0.95, value / maxV) : 0
            return `rgba(41, 93, 119, ${alpha})`
          },
          borderColor: hasStops ? "rgba(41, 93, 119, 0.15)" : "rgba(41, 93, 119, 0.2)",
          borderWidth: 1,
          width: ({ chart }) => {
            const area = chart.chartArea
            if (!area) return 20
            const xLabels = chart.scales.x?.ticks?.length || 1
            return (area.right - area.left) / Math.max(xLabels, 1) - 2
          },
          height: ({ chart }) => {
            const area = chart.chartArea
            if (!area) return 20
            if (isPhase2) {
              const yLabels = chart.scales.y?.ticks?.length || 1
              return (area.bottom - area.top) / Math.max(yLabels, 1) - 2
            }
            return (area.bottom - area.top) / 24 - 2
          }
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              title: (items) => {
                const p = items[0]?.raw
                if (!p) return ""
                if (titleT) {
                  return titleT.replace(/%\{(\w+)\}/g, (_, k) => p[k === 'y_full' ? '_full' : k] ?? p[k] ?? '')
                }
                return `${p.x} @ ${p.y}:00`
              },
              label: (item) => {
                const p = item.raw || {}
                if (labelT) {
                  return labelT.replace(/%\{(\w+)\}/g, (_, k) => p[k] ?? '')
                }
                return `${p.v || 0} leads`
              }
            }
          },
          datalabels: { display: false }
        },
        scales: isPhase2
          ? {
              x: { type: "category", labels: xLabels, offset: true, grid: { display: false }, ticks: { maxRotation: 45, minRotation: 0 } },
              y: { type: "category", labels: yLabels, offset: true, grid: { display: false }, ticks: { autoSkip: false } }
            }
          : {
              x: { type: "category", labels: xLabels, grid: { display: false }, ticks: { maxRotation: 45, minRotation: 0 } },
              y: {
                type: "linear",
                reverse: false,
                min: 0,
                max: 23,
                ticks: { stepSize: 2, callback: (v) => `${v}:00` },
                grid: { display: false }
              }
            }
      }
    }
  }

  // Linear interpolation across an ordered hex-color stop array.
  // Used by Phase 2 matrix heatmaps (Task 6) when a scale.stops array
  // is supplied via data-chart-options-value. Returns rgba() string.
  _interpolateStops(stops, t) {
    if (stops.length === 1) return stops[0]
    const scaled = t * (stops.length - 1)
    const i = Math.floor(scaled)
    const frac = scaled - i
    const a = this._hexToRgb(stops[i])
    const b = this._hexToRgb(stops[Math.min(i + 1, stops.length - 1)])
    const r = Math.round(a[0] + (b[0] - a[0]) * frac)
    const g = Math.round(a[1] + (b[1] - a[1]) * frac)
    const bl = Math.round(a[2] + (b[2] - a[2]) * frac)
    return `rgba(${r}, ${g}, ${bl}, 0.95)`
  }

  _hexToRgb(hex) {
    const h = String(hex).replace('#', '')
    return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)]
  }

  _normalizeSeries(input) {
    if (!input) return { labels: [], values: [] }

    // {date => count} hash
    if (!Array.isArray(input) && typeof input === "object" && !input.datasets) {
      const entries = Object.entries(input)
      return {
        labels: entries.map(([k]) => k),
        values: entries.map(([, v]) => Number(v) || 0)
      }
    }

    // [[label, value], ...] tuples
    if (Array.isArray(input) && input.every(e => Array.isArray(e) && e.length === 2)) {
      return {
        labels: input.map(([k]) => k),
        values: input.map(([, v]) => Number(v) || 0)
      }
    }

    // Chart.js native { labels: [], datasets: [{ data: [] }] }
    if (input.datasets && Array.isArray(input.datasets) && input.datasets[0]) {
      return {
        labels: input.labels || [],
        values: input.datasets[0].data || []
      }
    }

    return { labels: [], values: [] }
  }

  // Single options-merge chokepoint — every chart type's config (bar, area,
  // matrix, sparkline, column, line) funnels through here on its way into
  // `new Chart(...)`. The drilldown `onClick` is injected HERE so it lands on
  // every type uniformly; placing it in a per-type config or `_defaultOptions`
  // would silently miss the types that build their own options object.
  _mergeOptions(base, override) {
    let merged = base
    if (override && Object.keys(override).length > 0) {
      merged = {
        ...base,
        ...override,
        plugins: { ...(base.plugins || {}), ...(override.plugins || {}) },
        scales:  { ...(base.scales  || {}), ...(override.scales  || {}) }
      }
    }
    return this._withDrillOnClick(merged)
  }

  // Wraps the merged options with a drilldown `onClick`. No-op visually
  // unless the host <canvas> carries a `data-drill` attribute, so this is
  // dormant until a partial opts a chart into drilldowns. Any pre-existing
  // `onClick` is preserved by composition (it runs first).
  _withDrillOnClick(options) {
    const priorOnClick = options && options.onClick
    return {
      ...(options || {}),
      onClick: (event, _elements, chart) => {
        if (typeof priorOnClick === "function") {
          priorOnClick.call(chart, event, _elements, chart)
        }
        const drill = this.element.dataset.drill
        if (!drill) return
        let cfg
        try {
          cfg = JSON.parse(drill)
        } catch (e) {
          console.warn("chart controller: data-drill is not valid JSON:", e.message)
          return
        }
        // cfg = { param, origin, anchor, values?, paramPairs? }
        const hit = chart.getElementsAtEventForMode(event, "nearest", { intersect: true }, true)
        if (!hit.length) return
        const { index } = hit[0]
        let filters
        if (cfg.paramPairs) {
          // Multi-param-per-click (e.g. quality_min + quality_max); the
          // paramPairs array is parallel to chart.data.labels by index.
          filters = cfg.paramPairs[index]
        } else {
          const value = cfg.values ? cfg.values[index] : chart.data.labels[index]
          filters = { [cfg.param]: value }
        }
        if (!filters) return
        leadDrill(filters, { origin: cfg.origin, anchor: cfg.anchor })
      }
    }
  }

  _registerTurboCacheCleanup() {
    document.addEventListener("turbo:before-cache", this._onBeforeCache)
    document.addEventListener("turbo:render", this._onTurboRender)
  }

  _onBeforeCache = () => {
    this._destroyChart()
  }

  // LRPO-5694 — the before-cache listener above is DOCUMENT-level, so it also
  // fires on a controller that has already reconnected for the INCOMING page,
  // destroying the chart it built moments earlier. Measured order on a tab
  // round-trip: connect(built) -> disconnect -> connect(built) -> before-cache.
  // Rebuild after that teardown, on the same navigation. This mirrors
  // multi_heatmap_controller, which pairs the same before-cache teardown with a
  // turbo:render rebuild for exactly this reason.
  //
  // Guards: skip when the element has left the DOM (the controller is being torn
  // down for real), and skip when a chart already exists (a normal render where
  // before-cache never ran must not build a second instance on the same canvas).
  _onTurboRender = () => {
    if (!this.element || !this.element.isConnected) return
    if (this.chart) return

    this.chart = this._buildChart()
  }

  _destroyChart() {
    try {
      this.chart?.destroy()
    } catch (e) {
      // Chart.js sometimes throws if already destroyed — swallow.
    }
    this.chart = null
    if (this._sparkTipEl) {
      this._sparkTipEl.remove()
      this._sparkTipEl = null
    }
  }
}
